import { apiFetch } from '@/shared/api/client';
import type { MovimientoCajasFields } from '@/shared/api/controllers/movimientoCajas';
import type { CasaDolar, VistaMoneda } from '@/shared/lib/tipoCambio';

/**
 * Tesorería API wrappers. T1 ships read-only browsing; T2-T5 add
 * mutation endpoints as they land.
 */

export type MovimientoRow = {
  id: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  fields: MovimientoCajasFields;
  /** Running balance up to and including this row (chronological). */
  saldoAt: number;
};

export type MovimientosResponse = {
  rows: MovimientoRow[];
  totals: {
    ingresos: number;
    egresos: number;
    saldoActual: number;
    moneda: 'ARS' | 'MXN' | 'USD';
  };
  caja: string;
};

export type MovimientosQueryParams = {
  caja: string;
};

/**
 * Una fila por caja activa para la vista índice: las mismas tres cifras que
 * el footer del detalle (Ingresos / Egresos / Saldo del período activo) más
 * el último cierre. Las calcula el server con el mismo helper que el detalle.
 */
export type ResumenCajaRow = {
  caja: string;
  /** `Tipo_MDP` — Efectivo / Banco / Billetera Virtual. Sólo para el ícono. */
  tipo: string;
  /** `Categoria_MDP` — Local / Internacional. */
  categoria: string;
  /** `LlevaFactura_MDP` — etiqueta verde "Lleva factura". */
  llevaFactura: boolean;
  moneda: 'ARS' | 'MXN' | 'USD';
  saldo: number;
  ingresos: number;
  egresos: number;
  /** `dd/mm/yyyy`, o null si la caja nunca se cerró. */
  ultimoCierre: string | null;
  /**
   * Moneda y saldo que tenía la fila antes de pasarla a dólares, o `null` si
   * no se convirtió. En la vista USD conviven cajas convertidas y cajas que
   * siempre operaron en dólares — la tabla las distingue con esto.
   */
  convertidaDesde: { moneda: 'ARS' | 'MXN' | 'USD'; saldo: number } | null;
};

export type ResumenCajasResponse = {
  cajas: ResumenCajaRow[];
  /**
   * Cotización con la que se convirtió, o `null` cuando la vista es en
   * moneda local — o cuando se pidió USD y no se pudo traer la serie (ahí
   * las cajas vuelven en moneda local y la pantalla lo avisa).
   */
  cotizacion: { casa: string; fecha: string; valor: number } | null;
};

/**
 * `vista='usd'` convierte las cajas en moneda local con la última rueda de
 * la casa elegida; las cajas que ya operan en dólares no se tocan. Sólo AR
 * — no hay serie mexicana.
 */
export async function listResumenCajas(params?: {
  vista?: VistaMoneda;
  casa?: CasaDolar;
}): Promise<ResumenCajasResponse> {
  const query: Record<string, string> = {};
  if (params?.vista === 'usd') {
    query.vista = 'usd';
    if (params.casa) query.casa = params.casa;
  }
  return apiFetch<ResumenCajasResponse>('/api/tesoreria/resumen', { query });
}

/** Cross-month current balance for a caja. Used by the Cierre dialog. */
export async function getCajaSaldo(caja: string): Promise<{
  caja: string;
  saldo: number;
  moneda: 'ARS' | 'MXN' | 'USD';
}> {
  return apiFetch('/api/tesoreria/saldo', { query: { caja } });
}

export async function listMovimientos(
  params: MovimientosQueryParams,
): Promise<MovimientosResponse> {
  return apiFetch<MovimientosResponse>('/api/tesoreria/movimientos', {
    query: { caja: params.caja },
  });
}

// --------------------------------------------------------------------------
// Create (T2 — Agregar Ingreso/Egreso)
// --------------------------------------------------------------------------

export type CrearMovimientoPayload = {
  tipo: 'Ingreso' | 'Egreso';
  caja: string;
  monto: number;
  categoria: string;
  cuentaContable: string;
  descripcion: string;
  observaciones?: string;
  /** Pesos por USD. Obligatorio cuando la caja opera en dólares. */
  tipoCambio?: number;
};

export type CrearMovimientoResponse =
  | {
      outcome: 'created';
      id: string;
      impuestoRows: number;
      /** Σ of auto-generated impuesto amounts (ARS). */
      impuestoTotal: number;
    }
  | {
      outcome: 'insufficient_saldo';
      saldoAntes: number;
      message: string;
    };

export async function crearMovimiento(
  payload: CrearMovimientoPayload,
): Promise<CrearMovimientoResponse> {
  return apiFetch<CrearMovimientoResponse>('/api/tesoreria/movimiento', {
    method: 'POST',
    body: payload,
  });
}

// --------------------------------------------------------------------------
// Cargos MercadoPago (botón amarillo): N egresos hijos de un cobro/pago MP
// --------------------------------------------------------------------------

export type RegistrarCargosMpPayload = {
  caja: string;
  cargos: Array<{ categoria: string; cuentaContable: string; monto: number }>;
  observaciones?: string;
  /** Pesos por USD. Obligatorio cuando la caja opera en dólares. */
  tipoCambio?: number;
};

export type RegistrarCargosMpResponse =
  | {
      outcome: 'created';
      cargos: number;
      total: number;
      /** Nova MP: los cargos se netearon contra Caja Diferencia. */
      neteoCajaDiferencia: boolean;
    }
  | {
      outcome: 'insufficient_saldo' | 'insufficient_caja_diferencia' | 'blocked_par_invalido';
      message: string;
    };

export async function registrarCargosMp(
  parentId: string,
  payload: RegistrarCargosMpPayload,
): Promise<RegistrarCargosMpResponse> {
  return apiFetch<RegistrarCargosMpResponse>(
    `/api/tesoreria/movimiento/${encodeURIComponent(parentId)}/cargos-mp`,
    { method: 'POST', body: payload },
  );
}

// --------------------------------------------------------------------------
// Anular / Edit (manual rows only)
// --------------------------------------------------------------------------

export type AnularMovimientoResponse =
  | { outcome: 'anulado'; id: string; impuestosAnulados: number }
  | { outcome: 'already_anulado' }
  | { outcome: 'blocked_auto'; message: string }
  | { outcome: 'blocked_cierre'; message: string };

export async function anularMovimiento(
  id: string,
): Promise<AnularMovimientoResponse> {
  return apiFetch<AnularMovimientoResponse>(
    `/api/tesoreria/movimiento/${encodeURIComponent(id)}/anular`,
    { method: 'POST' },
  );
}

export type RevertirCobroResponse =
  | {
      outcome: 'revertido';
      comprobanteId: string;
      operacionId: string;
      movimientosAnulados: number;
      medioPago: string;
      statusRO: string;
    }
  | {
      outcome: 'blocked_estado_cobro' | 'blocked_nova' | 'blocked_estado_venta' | 'blocked_cierre';
      message: string;
    };

/**
 * Deshace la confirmación del cobro de un movimiento: anula sus movimientos,
 * el comprobante vuelve a Cobros a Confirmar (con `medioPago` si se corrige la
 * caja) y la venta/stock vuelven atrás. Admin y administración.
 */
export async function revertirCobro(
  comprobanteId: string,
  medioPago?: string,
): Promise<RevertirCobroResponse> {
  return apiFetch<RevertirCobroResponse>(
    `/api/administracion/cobros/${encodeURIComponent(comprobanteId)}/revertir`,
    { method: 'POST', body: medioPago ? { medioPago } : {} },
  );
}

export type EditMovimientoPayload = {
  /** Sólo para completar la cotización faltante de un movimiento en
   *  dólares. El backend rechaza el pedido si la fila ya la tiene. */
  tipoCambio?: number;
  descripcion: string;
  observaciones?: string;
};

export type EditMovimientoResponse =
  | { outcome: 'blocked_fx_no_usd'; message: string }
  | { outcome: 'blocked_fx_existente'; message: string }
  | { outcome: 'blocked_fx_sin_monto'; message: string }
  | { outcome: 'updated'; id: string; cotizacionCargada?: boolean }
  | { outcome: 'blocked_auto'; message: string }
  | { outcome: 'blocked_anulado'; message: string };

export async function editMovimiento(
  id: string,
  payload: EditMovimientoPayload,
): Promise<EditMovimientoResponse> {
  return apiFetch<EditMovimientoResponse>(
    `/api/tesoreria/movimiento/${encodeURIComponent(id)}`,
    { method: 'PUT', body: payload },
  );
}

// --------------------------------------------------------------------------
// T3 — Transferencia / Depósito
// --------------------------------------------------------------------------

export type TransferenciaPayload = {
  kind: 'Transferencia' | 'Deposito';
  origen: string;
  destino: string;
  monto: number;
  /** Required when origen + destino are in different currencies. */
  tipoCambio?: number;
  /** Required when cross-currency. Amount in destino's native currency. */
  montoDestino?: number;
  observaciones?: string;
};

export type TransferenciaResponse =
  | {
      outcome: 'created';
      idOperacion: string;
      egresoId: string;
      ingresoId: string;
      impuestoRows: number;
    }
  | { outcome: 'invalid_cajas'; message: string }
  | { outcome: 'insufficient_saldo'; saldoAntes: number; message: string };

export async function crearTransferencia(
  payload: TransferenciaPayload,
): Promise<TransferenciaResponse> {
  return apiFetch<TransferenciaResponse>('/api/tesoreria/transferencia', {
    method: 'POST',
    body: payload,
  });
}

// --------------------------------------------------------------------------
// T4 — Cierre de Caja
// --------------------------------------------------------------------------

export type CierreCajaPayload = {
  caja: string;
  balanceFisico: number;
  observaciones?: string;
};

export type CierreCajaResponse = {
  outcome: 'closed';
  idCierre: string;
  saldoBook: number;
  balanceFisico: number;
  /** `balanceFisico − saldoBook`. Positive = surplus (Ingreso ajuste), negative = shortage (Egreso ajuste). */
  diferencia: number;
  ajusteId: string | null;
  cierreId: string;
  aperturaId: string;
  rowsTagged: number;
};

export async function crearCierreCaja(
  payload: CierreCajaPayload,
): Promise<CierreCajaResponse> {
  return apiFetch<CierreCajaResponse>('/api/tesoreria/cierre', {
    method: 'POST',
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// Reclasificación de cuenta contable
// ---------------------------------------------------------------------------

export type ReclasificarCuentaPayload = {
  /** Par del catálogo: los dos van juntos siempre. */
  categoria: string;
  cuentaContable: string;
  motivo?: string;
};

export type ReclasificarCuentaResponse =
  | { outcome: 'updated'; id: string; categoria: string; cuentaContable: string }
  | { outcome: 'sin_cambios'; id: string }
  | {
      outcome:
        | 'blocked_anulado'
        | 'blocked_estructural'
        | 'blocked_par_invalido'
        | 'blocked_cuenta_inactiva';
      message: string;
    };

/**
 * Cambia la imputación contable de un movimiento ya asentado.
 *
 * Manda SIEMPRE el par (categoría + cuenta): `cash_movements` se une al plan
 * de cuentas por el par de texto, no por FK, así que mandar sólo la cuenta
 * dejaría la fila fuera del Estado Financiero.
 */
export async function reclasificarCuentaContable(
  movimientoId: string | number,
  payload: ReclasificarCuentaPayload,
): Promise<ReclasificarCuentaResponse> {
  return apiFetch<ReclasificarCuentaResponse>(
    `/api/tesoreria/movimiento/${movimientoId}/cuenta-contable`,
    { method: 'PUT', body: payload },
  );
}

export type TrasladarCajaResponse =
  | { outcome: 'updated'; id: string; cajaOrigen: string; cajaDestino: string }
  | { outcome: string; message: string; saldoAntes?: number };

/**
 * Pasa un Ingreso/Egreso manual a otra caja de la misma moneda (p. ej.
 * Efectivo Pesos → Efectivo Pesos N). Sólo cambia la caja; el server valida
 * que no tenga impuestos asociados ni deje saldos en negativo.
 */
export async function trasladarMovimientoCaja(
  movimientoId: string | number,
  cajaDestino: string,
): Promise<TrasladarCajaResponse> {
  return apiFetch<TrasladarCajaResponse>(`/api/tesoreria/movimiento/${movimientoId}/caja`, {
    method: 'PUT',
    body: { cajaDestino },
  });
}
